import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateTable from '../components/CandidateTable';
import CandidateModal from '../components/CandidateModal';
import useAuthStore from '../stores/authStore';
import useConnectionStore from '../stores/connectionStore';
import './MyConnections.css';

const MyConnections = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const token = useAuthStore((state) => state.token);
  const { connectionProfiles, isLoadingProfiles, fetchConnectionProfiles } = useConnectionStore();
  
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    fetchConnectionProfiles(token);
  }, [isAuthenticated, token, navigate, fetchConnectionProfiles]);

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleCloseModal = () => {
    setSelectedCandidate(null);
  };

  // Calculate total count for "All" tab
  const totalCount = connectionProfiles.favorite.length + 
                     connectionProfiles.requestSent.length + 
                     connectionProfiles.connected.length;

  const tabs = [
    { id: 'all', label: 'All', count: totalCount },
    { id: 'favorite', label: 'Favorites', count: connectionProfiles.favorite.length },
    { id: 'requestSent', label: 'Requests Sent', count: connectionProfiles.requestSent.length },
    { id: 'connected', label: 'Connected', count: connectionProfiles.connected.length }
  ];

  // Get profiles based on active tab
  const getCurrentProfiles = () => {
    if (activeTab === 'all') {
      return [
        ...connectionProfiles.favorite,
        ...connectionProfiles.requestSent,
        ...connectionProfiles.connected
      ];
    }
    return connectionProfiles[activeTab] || [];
  };

  const currentProfiles = getCurrentProfiles();

  return (
    <div className="my-connections">
      <div className="connections-header">
        <h1>My Connections</h1>
        <p>Manage your co-founder connections, favorites, and sent requests</p>
      </div>

      <div className="connections-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span className="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="connections-content">
        {isLoadingProfiles ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading connections...</p>
          </div>
        ) : currentProfiles.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">
              {activeTab === 'all' && '📋'}
              {activeTab === 'favorite' && '⭐'}
              {activeTab === 'requestSent' && '📤'}
              {activeTab === 'connected' && '🤝'}
            </span>
            <h3>No {activeTab === 'all' ? 'connections' : tabs.find(t => t.id === activeTab)?.label.toLowerCase()} yet</h3>
            <p>
              {activeTab === 'all' && 'Start connecting with potential co-founders to see them here'}
              {activeTab === 'favorite' && 'Mark candidates as favorites to keep track of them'}
              {activeTab === 'requestSent' && 'Send connection requests to candidates you\'re interested in'}
              {activeTab === 'connected' && 'Connect with potential co-founders to see them here'}
            </p>
            <button className="browse-btn" onClick={() => navigate('/')}>
              Browse Candidates
            </button>
          </div>
        ) : (
          <CandidateTable
            candidates={currentProfiles}
            onCandidateClick={handleCandidateClick}
          />
        )}
      </div>

      {selectedCandidate && (
        <CandidateModal
          candidate={selectedCandidate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MyConnections;
